import { useDateFormat, useTimeFormat, useTimezone } from "@/utils/settings";
import { format, parseISO, formatDistanceToNow, isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export const formatDate = (date: string | Date | null | undefined, includeTime: boolean = false) => {
	// Return a placeholder if date is null, undefined, or invalid
	if (!date) return "Not specified";
	
	// Try to create a valid date object
	const dateObj = typeof date === 'string' ? parseISO(date) : date;
	
	// Check if date is valid before formatting
	if (isNaN(dateObj.getTime())) {
		console.error("Invalid date in formatDate:", date);
		return "Invalid date";
	}
	
	try {
		const dateFormat = useDateFormat();
		const timeFormat = useTimeFormat();
		const timezone = useTimezone();
		
		// Convert date format from settings to date-fns format
		const dateFormatMapping: { [key: string]: string } = {
			'MMM-d-yyyy': 'MMM d, yyyy',
			'MM-dd-yyyy': 'MM/dd/yyyy',
			'dd-MM-yyyy': 'dd/MM/yyyy',
			'yyyy-MM-dd': 'yyyy-MM-dd'
		};
		
		const timeFormatMapping: { [key: string]: string } = {
			'hh-mm-a': 'h:mm a',
			'HH-mm': 'HH:mm',
			'h-mm-a': 'h:mm a', 
			'HH-mm-ss': 'HH:mm:ss'
		};
		
		// Use date-fns-tz to handle timezone properly
		const formattedDate = formatInTimeZone(
			dateObj,
			timezone || 'UTC',
			dateFormatMapping[dateFormat] || 'MMM d, yyyy'
		);
		
		if (includeTime) {
			const formattedTime = formatInTimeZone(
				dateObj,
				timezone || 'UTC',
				timeFormatMapping[timeFormat] || 'h:mm a'
			);
			return `${formattedDate} ${formattedTime}`;
		}
		
		return formattedDate;
	} catch (error) {
		console.error('Error formatting date:', error, date);
		return 'Date error';
	}
};

// New function specifically for calendar date formatting
export const formatCalendarDate = (date: Date, formatType: 'date' | 'time' | 'dateTime' = 'date', isAllDay: boolean = false) => {
	try {
		const dateFormat = useDateFormat();
		const timeFormat = useTimeFormat();
		const timezone = useTimezone();

		// Convert date format from settings to date-fns format
		const dateFormatMapping: { [key: string]: string } = {
			'MMM-d-yyyy': 'MMM d, yyyy',
			'MM-dd-yyyy': 'MM/dd/yyyy',
			'dd-MM-yyyy': 'dd/MM/yyyy',
			'yyyy-MM-dd': 'yyyy-MM-dd'
		};

		const timeFormatMapping: { [key: string]: string } = {
			'hh-mm-a': 'h:mm a',
			'HH-mm': 'HH:mm',
			'h-mm-a': 'h:mm a',
			'HH-mm-ss': 'HH:mm:ss'
		};

		// Get the format strings
		const dateFormatStr = dateFormatMapping[dateFormat] || 'MMM d, yyyy';
		const timeFormatStr = timeFormatMapping[timeFormat] || 'h:mm a';

		// For all-day events, use plain format() without timezone conversion
		// since they represent calendar days, not specific moments in time
		if (isAllDay && formatType === 'date') {
			return format(date, dateFormatStr);
		}

		// Format based on type with timezone conversion for timed events
		switch (formatType) {
			case 'date':
				return formatInTimeZone(date, timezone || 'UTC', dateFormatStr);
			case 'time':
				return formatInTimeZone(date, timezone || 'UTC', timeFormatStr);
			case 'dateTime':
				return formatInTimeZone(date, timezone || 'UTC', `${dateFormatStr} ${timeFormatStr}`);
			default:
				return formatInTimeZone(date, timezone || 'UTC', dateFormatStr);
		}
	} catch (error) {
		console.error('Error formatting calendar date:', error, date);
		return 'Date error';
	}
};

export const formatPrice = (amount: number, currency: string = 'usd'): string => {
	// Convert from cents to actual currency amount
	const actualAmount = amount / 100;
	
	// Currency symbol mapping
	const currencySymbols: { [key: string]: string } = {
		'usd': '$',
		'eur': '€',
		'gbp': '£',
		'cad': 'CA$',
		'aud': 'A$',
		'jpy': '¥',
		'sek': 'kr',
		'nok': 'kr',
		'dkk': 'kr',
		'chf': 'CHF',
		'pln': 'zł'
	};
	
	const symbol = currencySymbols[currency.toLowerCase()] || currency.toUpperCase();
	
	// Format with appropriate decimal places (JPY doesn't use decimals)
	const decimals = currency.toLowerCase() === 'jpy' ? 0 : 2;
	
	return `${symbol}${actualAmount.toFixed(decimals)}`;
};

export const formatUnixTimestamp = (timestamp: number, includeTime: boolean = false): string => {
	if (!timestamp) return "Not specified";
	
	try {
		// Convert Unix timestamp (seconds) to Date object
		const date = new Date(timestamp * 1000);
		return formatDate(date, includeTime);
	} catch (error) {
		console.error("Error formatting Unix timestamp:", error);
		return "Invalid date";
	}
};

/**
 * Utility function to format "Last used" dates for experience data
 * Fixes the issue where dates show "2025y ago" instead of correct years
 */
export const formatExperienceLastUsed = (dateString: string | Date | null | undefined): string => {
	// Return a placeholder if date is null, undefined, or empty
	if (!dateString) return "Never used";
	
	try {
		// Parse the date string if it's a string
		const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
		
		// Check if date is valid
		if (!isValid(date)) {
			console.warn("Invalid date in formatExperienceLastUsed:", dateString);
			return "Invalid date";
		}
		
		// Get current date to ensure we're using the correct year
		const now = new Date();
		
		// Calculate the difference in years
		const diffInYears = now.getFullYear() - date.getFullYear();
		
		// If the date is in the future, return a warning
		if (diffInYears < 0) {
			return "Future date";
		}
		
		// If it's the same year, use a more precise calculation
		if (diffInYears === 0) {
			const diffInMonths = now.getMonth() - date.getMonth();
			if (diffInMonths === 0) {
				const diffInDays = now.getDate() - date.getDate();
				if (diffInDays === 0) {
					return "Today";
				} else if (diffInDays === 1) {
					return "Yesterday";
				} else {
					return `${diffInDays} days ago`;
				}
			} else {
				return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
			}
		}
		
		// For years, use the calculated difference
		return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
		
	} catch (error) {
		console.error("Error formatting experience last used date:", error, dateString);
		return "Date error";
	}
};

/**
 * Alternative implementation using date-fns formatDistanceToNow
 * This is more robust and handles edge cases better
 */
const formatExperienceLastUsedWithDateFns = (dateString: string | Date | null | undefined): string => {
	// Return a placeholder if date is null, undefined, or empty
	if (!dateString) return "Never used";
	
	try {
		// Parse the date string if it's a string
		const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
		
		// Check if date is valid
		if (!isValid(date)) {
			console.warn("Invalid date in formatExperienceLastUsedWithDateFns:", dateString);
			return "Invalid date";
		}
		
		// Get current date to ensure we're using the correct year
		const now = new Date();
		
		// If the date is in the future, return a warning
		if (date > now) {
			return "Future date";
		}
		
		// Use date-fns formatDistanceToNow for consistent formatting
		const distance = formatDistanceToNow(date, { addSuffix: true });
		
		// Ensure the result doesn't contain "2025y" or similar incorrect years
		if (distance.includes("2025")) {
			console.warn("Date calculation returned 2025, recalculating:", dateString);
			// Fallback to manual calculation
			const diffInYears = now.getFullYear() - date.getFullYear();
			return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
		}
		
		return distance;
		
	} catch (error) {
		console.error("Error formatting experience last used date with date-fns:", error, dateString);
		return "Date error";
	}
};
